import { ROLES } from '@giftsay/constants';
import { STORE_STATUSES } from '@giftsay/types';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@giftsay/ui';
import {
  AlertTriangle,
  ChevronDown,
  Contact,
  CreditCard,
  FileText,
  FolderTree,
  Gift,
  LayoutDashboard,
  LayoutTemplate,
  LogOut,
  Mail,
  Menu,
  Navigation,
  Package,
  PackageCheck,
  Palette,
  Percent,
  Phone,
  ScrollText,
  Settings,
  ShoppingBag,
  Star,
  Store,
  Tag,
  Ticket,
  Truck,
  UserCog,
  Users,
  X,
  Rocket,
} from 'lucide-react';
import * as React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAppSelector } from '../../store/hooks.js';
import { signOut } from '../../features/auth/services/firebase-auth.js';
import { ThemeToggle } from '../theme/ThemeToggle.js';
import { OrderNotificationBell } from '../../features/orders/components/OrderNotificationBell.js';
import { PaymentProviderAlert } from '../../features/payments/components/PaymentProviderAlert.js';
import { staffModuleForPath, useStaffAccess } from '../../features/staff/hooks/use-staff-access.js';
import { useVendorStoreStatus } from '../../features/stores/hooks/use-store-notifications.js';
import { StoreSuspendedNotice } from './StoreSuspendedNotice.js';

interface NavLinkItem {
  type: 'link';
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end: boolean;
}

interface NavGroupItem {
  type: 'group';
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children: {
    to: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    /** True when this child's path is a prefix of a sibling's (e.g. `/reviews` vs `/reviews/settings`) — without it, NavLink would also highlight this child on the sibling's route. */
    end?: boolean;
  }[];
}

const NAV_SECTIONS: { title: string; items: (NavLinkItem | NavGroupItem)[] }[] = [
  {
    title: 'Overview',
    items: [{ type: 'link', to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true }],
  },
  {
    title: 'Catalog',
    items: [
      { type: 'link', to: '/taxonomy', label: 'Taxonomy', icon: FolderTree, end: false },
      { type: 'link', to: '/products', label: 'Products', icon: Package, end: false },
    ],
  },
  {
    title: 'Merchandising',
    items: [
      {
        type: 'link',
        to: '/homepage-builder',
        label: 'Homepage Builder',
        icon: LayoutTemplate,
        end: false,
      },
      { type: 'link', to: '/navigation', label: 'Navigation', icon: Navigation, end: false },
      {
        type: 'group',
        label: 'Promotions',
        icon: Tag,
        children: [
          { to: '/promotions/badges', label: 'Badges', icon: Tag },
          { to: '/promotions/coupons', label: 'Coupons', icon: Ticket },
          { to: '/promotions/discounts', label: 'Discounts', icon: Percent },
        ],
      },
    ],
  },
  {
    title: 'Operations',
    items: [
      { type: 'link', to: '/orders', label: 'Orders', icon: ShoppingBag, end: false },
      { type: 'link', to: '/customers', label: 'Customers', icon: Contact, end: false },
      {
        type: 'link',
        to: '/payment-discrepancies',
        label: 'Payment Issues',
        icon: AlertTriangle,
        end: false,
      },
      {
        type: 'link',
        to: '/payments/audit-log',
        label: 'Payment Audit Log',
        icon: ScrollText,
        end: false,
      },
      { type: 'link', to: '/delivery-zones', label: 'Delivery Zones', icon: Truck, end: false },
      {
        type: 'group',
        label: 'Reviews',
        icon: Star,
        children: [
          { to: '/reviews', label: 'All Reviews', icon: Star, end: true },
          { to: '/reviews/settings', label: 'Settings', icon: Settings },
        ],
      },
    ],
  },
  {
    title: 'Account',
    items: [
      { type: 'link', to: '/stores', label: 'Stores', icon: Store, end: false },
      { type: 'link', to: '/users', label: 'Users', icon: Users, end: false },
      {
        type: 'group',
        label: 'Settings',
        icon: Settings,
        children: [
          { to: '/settings/contact', label: 'Contact', icon: Phone },
          { to: '/settings/payments', label: 'Payments', icon: CreditCard },
          { to: '/settings/email', label: 'Email', icon: Mail },
          { to: '/settings/branding', label: 'Branding', icon: Palette },
          { to: '/settings/shipping', label: 'Shipping', icon: PackageCheck },
          { to: '/settings/pages', label: 'Pages', icon: FileText },
        ],
      },
    ],
  },
];

/** These affect or expose every store, so only super admins get them. */
const SUPER_ADMIN_NAV_SECTION: { title: string; items: (NavLinkItem | NavGroupItem)[] } = {
  title: 'Platform',
  items: [
    { type: 'link', to: '/releases', label: 'Releases', icon: Rocket, end: false },
    { type: 'link', to: '/api-errors', label: 'API Errors', icon: AlertTriangle, end: false },
  ],
};

/**
 * A vendor manages their own store's catalog, merchandising and fulfillment — no users, stores,
 * or platform settings. These link to the same pages staff use; the API scopes every read and
 * write to the vendor's store, so the pages need no separate vendor copy.
 */
const VENDOR_NAV_SECTIONS: { title: string; items: (NavLinkItem | NavGroupItem)[] }[] = [
  {
    title: 'Overview',
    items: [{ type: 'link', to: '/', label: 'Home', icon: LayoutDashboard, end: true }],
  },
  {
    title: 'Catalog',
    items: [
      { type: 'link', to: '/taxonomy', label: 'Taxonomy', icon: FolderTree, end: false },
      { type: 'link', to: '/products', label: 'Products', icon: Package, end: false },
    ],
  },
  {
    title: 'Merchandising',
    items: [
      {
        type: 'link',
        to: '/homepage-builder',
        label: 'Homepage Builder',
        icon: LayoutTemplate,
        end: false,
      },
      { type: 'link', to: '/navigation', label: 'Navigation', icon: Navigation, end: false },
      {
        type: 'group',
        label: 'Promotions',
        icon: Tag,
        children: [
          { to: '/promotions/coupons', label: 'Coupons', icon: Ticket },
          { to: '/promotions/discounts', label: 'Discounts', icon: Percent },
        ],
      },
    ],
  },
  {
    title: 'Operations',
    items: [
      { type: 'link', to: '/orders', label: 'Orders', icon: ShoppingBag, end: false },
      { type: 'link', to: '/customers', label: 'Customers', icon: Contact, end: false },
      { type: 'link', to: '/delivery-zones', label: 'Delivery Zones', icon: Truck, end: false },
      {
        type: 'group',
        label: 'Reviews',
        icon: Star,
        children: [
          { to: '/reviews', label: 'All Reviews', icon: Star, end: true },
          { to: '/reviews/settings', label: 'Settings', icon: Settings },
        ],
      },
    ],
  },
  {
    title: 'Settings',
    items: [
      { type: 'link', to: '/settings/contact', label: 'Contact', icon: Phone, end: false },
      { type: 'link', to: '/settings/email', label: 'Email', icon: Mail, end: false },
      { type: 'link', to: '/settings/branding', label: 'Branding', icon: Palette, end: false },
      { type: 'link', to: '/settings/shipping', label: 'Shipping', icon: PackageCheck, end: false },
      { type: 'link', to: '/settings/pages', label: 'Pages', icon: FileText, end: false },
    ],
  },
];

/** Store-owner only — their staff never manage staff. */
const VENDOR_OWNER_NAV_SECTION: NavSection = {
  title: 'Team',
  items: [{ type: 'link', to: '/staff', label: 'Staff', icon: UserCog, end: false }],
};

type NavSection = { title: string; items: (NavLinkItem | NavGroupItem)[] };

/** Drops links a staff login's role can't view, then any group or section left empty. */
function filterNav(sections: NavSection[], canViewPath: (path: string) => boolean): NavSection[] {
  return sections
    .map(({ title, items }) => ({
      title,
      items: items.flatMap((item): (NavLinkItem | NavGroupItem)[] => {
        if (item.type === 'link') {
          return canViewPath(item.to) ? [item] : [];
        }
        const children = item.children.filter((child) => canViewPath(child.to));
        return children.length > 0 ? [{ ...item, children }] : [];
      }),
    }))
    .filter((section) => section.items.length > 0);
}

const NAV_LINK_CLASS = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2 rounded-md border-l-2 px-3 py-2 text-sm font-medium transition-colors ${
    isActive
      ? 'border-primary-foreground text-primary-foreground font-semibold'
      : 'border-transparent text-primary-foreground/75 hover:border-primary-foreground/40 hover:text-primary-foreground'
  }`;

/** A vendor (owner or staff) whose store platform admins have suspended sees a notice instead of
 *  the portal. The status comes from the store's live doc, so it applies the moment it changes. */
export function AdminShell() {
  const isVendor = useAppSelector((state) => state.auth.user?.role === ROLES.VENDOR);
  const storeStatus = useVendorStoreStatus();
  if (isVendor && storeStatus === STORE_STATUSES.SUSPENDED) {
    return <StoreSuspendedNotice />;
  }
  return <PortalShell />;
}

function PortalShell() {
  const user = useAppSelector((state) => state.auth.user);
  const location = useLocation();
  const isVendor = user?.role === ROLES.VENDOR;
  const { isStoreOwner, isStaffLogin, canViewPath, canEdit } = useStaffAccess();
  const navSections = isVendor
    ? filterNav(
        isStoreOwner ? [...VENDOR_NAV_SECTIONS, VENDOR_OWNER_NAV_SECTION] : VENDOR_NAV_SECTIONS,
        canViewPath,
      )
    : user?.role === ROLES.SUPER_ADMIN
      ? [...NAV_SECTIONS, SUPER_ADMIN_NAV_SECTION]
      : NAV_SECTIONS;
  const currentModule = staffModuleForPath(location.pathname);
  const isViewOnly = isStaffLogin && currentModule !== null && !canEdit(currentModule);

  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  const [openGroups, setOpenGroups] = React.useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const { items } of navSections) {
      for (const item of items) {
        if (
          item.type === 'group' &&
          item.children.some((child) => location.pathname.startsWith(child.to))
        ) {
          initial.add(item.label);
        }
      }
    }
    return initial;
  });

  React.useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  function toggleGroup(label: string) {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }

  return (
    <div className="flex min-h-screen">
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 shrink-0 flex-col overflow-y-auto bg-primary transition-transform duration-200 ease-in-out md:static md:z-auto md:w-60 md:translate-x-0 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between gap-2 px-6 font-display text-lg font-semibold text-primary-foreground">
          <span className="flex items-center gap-2">
            <Gift className="text-primary-foreground" />
            {isVendor ? 'Vendor Portal' : 'Giftsay Admin'}
          </span>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            className="rounded-md p-1 text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground md:hidden"
            aria-label="Close menu"
          >
            <X className="size-5" />
          </button>
        </div>
        <nav className="flex flex-col gap-1 px-3 pb-4">
          {navSections.map(({ title, items }) => (
            <div key={title} className="mb-1">
              <div className="px-3 pt-4 pb-1 text-xs font-semibold tracking-wide text-primary-foreground/60 uppercase">
                {title}
              </div>
              {items.map((item) => {
                if (item.type === 'link') {
                  const { to, label, icon: Icon, end } = item;
                  return (
                    <NavLink key={to} to={to} end={end} className={NAV_LINK_CLASS}>
                      <Icon className="size-4" />
                      {label}
                    </NavLink>
                  );
                }

                const { label, icon: Icon, children } = item;
                const isOpen = openGroups.has(label);
                const isParentActive = children.some((child) =>
                  location.pathname.startsWith(child.to),
                );

                return (
                  <div key={label}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(label)}
                      className={`flex w-full items-center gap-2 rounded-md border-l-2 px-3 py-2 text-sm font-medium transition-colors hover:border-primary-foreground/40 hover:text-primary-foreground ${
                        isParentActive
                          ? 'border-primary-foreground font-semibold text-primary-foreground'
                          : 'border-transparent text-primary-foreground/75'
                      }`}
                    >
                      <Icon className="size-4" />
                      {label}
                      <ChevronDown
                        className={`ml-auto size-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="ml-3.5 flex flex-col gap-0.5 border-l border-primary-foreground/20 pl-3">
                        {children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            end={child.end ?? false}
                            className={NAV_LINK_CLASS}
                          >
                            <child.icon className="size-3.5" />
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center gap-2 border-b border-border px-4 md:px-6">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground md:hidden"
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
          <div className="flex flex-1 items-center justify-end gap-3">
            <OrderNotificationBell />
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
                  <Avatar className="size-8">
                    <AvatarFallback>
                      {user?.displayName.slice(0, 2).toUpperCase() ?? '??'}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => void signOut()} variant="destructive">
                  <LogOut className="mr-2 size-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden">
          {isVendor ? null : <PaymentProviderAlert />}
          {isViewOnly && (
            <div className="border-b border-border bg-muted px-4 py-2 text-sm text-muted-foreground md:px-6">
              View only — your role can see this screen but not change anything on it.
            </div>
          )}
          <React.Suspense
            fallback={
              <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">
                Loading...
              </div>
            }
          >
            <Outlet />
          </React.Suspense>
        </main>
      </div>
    </div>
  );
}
